import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { pick } from '@/lib/i18n';
import { SiteRenderer } from '@/components/renderer/SiteRenderer';
import { WhatsAppFloat } from '@/components/renderer/WhatsAppFloat';
import { BlogPostView } from '@/components/renderer/ExtraBlocks';
import { LeadPopup, PasswordGate, NotFoundView, setTrackingSite, trackEvent } from '@/components/renderer/SiteExtras';

const PW_KEY = (slug) => `pillot_site_pw_${slug}`;

export default function PublicSitePage({ hostSite = null }) {
  const { slug, pageSlug, postSlug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const hostProtected = !!hostSite?.protected;
  const [site, setSite] = useState(hostProtected ? null : hostSite);
  const [status, setStatus] = useState(hostSite ? (hostProtected ? 'gate' : 'ok') : 'loading');
  const [gate, setGate] = useState(hostProtected ? { info: hostSite, busy: false, error: false } : null);
  const [post, setPost] = useState(null);
  const [lang, setLangState] = useState(() => params.get('lang') || localStorage.getItem('pillot_site_lang') || null);
  const isPreview = params.get('preview') === '1';
  const trackedPath = useRef(null);
  const siteSlug = hostSite?.slug || slug;

  const load = useCallback((pw) => {
    setStatus('loading');
    const headers = pw ? { 'X-Site-Password': pw } : {};
    const req = hostSite ? api.get('/public/sites/by-host', { params: { host: window.location.host }, headers }) : api.get(`/public/sites/by-slug/${slug}`, { params: { preview: isPreview }, headers });
    req.then(({ data }) => { setSite(data); setStatus('ok'); setGate(null); if (pw) sessionStorage.setItem(PW_KEY(siteSlug), pw); })
      .catch((e) => {
        if (e.response?.status === 401 && e.response.data?.protected) { setGate({ info: e.response.data, busy: false, error: !!pw }); setStatus('gate'); return; }
        setStatus(e.response?.status === 404 ? 'notfound' : 'error');
      });
  }, [slug, hostSite, isPreview, siteSlug]);

  useEffect(() => {
    const saved = sessionStorage.getItem(PW_KEY(siteSlug));
    if (hostSite && !saved) return;
    if (hostSite && !hostProtected) return;
    load(saved || undefined);
  }, [load]); // eslint-disable-line

  useEffect(() => { setTrackingSite(site?.is_published ? site.slug : null); }, [site?.slug, site?.is_published]);
  useEffect(() => {
    if (!postSlug || !siteSlug) { setPost(null); return; }
    setPost(null);
    api.get(`/public/sites/${siteSlug}/posts/${postSlug}`).then(({ data }) => setPost(data)).catch(() => setPost({ notfound: true }));
  }, [siteSlug, postSlug]);

  useEffect(() => {
    const lc = site?.livechat;
    if (!lc || isPreview || document.getElementById('pillot-livechat')) return;
    const s = document.createElement('script');
    s.id = 'pillot-livechat'; s.async = true;
    s.src = `${process.env.REACT_APP_BACKEND_URL}/api/pesan/widget.js?key=${encodeURIComponent(lc.key)}`;
    document.body.appendChild(s);
  }, [site?.livechat?.key]); // eslint-disable-line

  const effectiveLang = lang || site?.default_lang || 'id';
  const setLang = (l) => { localStorage.setItem('pillot_site_lang', l); setLangState(l); };

  // Redirect kustom (Pengaturan → Akses & Redirect)
  useEffect(() => {
    if (!site) return;
    const path = `/${pageSlug || ''}`.replace(/\/$/, '') || '/';
    const r = (site.settings?.redirects || []).find((x) => (x.from || '').replace(/\/$/, '') === path.replace(/\/$/, '') && x.to);
    if (r) {
      if (r.to.startsWith('http')) { window.location.replace(r.to); return; }
      const base = hostSite ? '' : `/site/${slug}`;
      navigate(`${base}${r.to.startsWith('/') ? r.to : `/${r.to}`}`.replace(/\/$/, '') || '/', { replace: true });
    }
  }, [site, pageSlug]); // eslint-disable-line

  useEffect(() => {
    if (!site) return;
    const page = site.pages?.find((p) => (pageSlug ? p.slug === pageSlug : p.is_home)) || (pageSlug ? null : site.pages?.[0]);
    const postTitle = post && !post.notfound ? pick(post.seo?.title, effectiveLang) || pick(post.title, effectiveLang) : null;
    const title = postTitle ? `${postTitle} · ${site.name}` : pick(page?.seo?.title, effectiveLang) || pick(site.seo?.title, effectiveLang) || `${pick(page?.title, effectiveLang)} · ${site.name}`;
    document.title = title;
    const desc = (post && !post.notfound ? pick(post.seo?.description, effectiveLang) || pick(post.excerpt, effectiveLang) : '') || pick(page?.seo?.description, effectiveLang) || pick(site.seo?.description, effectiveLang) || '';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = desc;
    document.documentElement.lang = effectiveLang;
    const setTag = (sel, attrs) => { let el = document.head.querySelector(sel); if (!el) { el = document.createElement(sel.startsWith('link') ? 'link' : 'meta'); document.head.appendChild(el); } Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v)); return el; };
    const seo = site.seo || {};
    const favicon = seo.favicon_url || site.navbar?.logo_url;
    if (favicon) setTag('link[rel="icon"]', { rel: 'icon', href: favicon });
    const url = window.location.href.split('#')[0];
    const image = (post && !post.notfound && post.cover_url) || pick(page?.seo?.og_image, effectiveLang) || page?.seo?.og_image || seo.og_image || page?.sections?.find((x) => x.type === 'hero')?.content?.image_url || '';
    setTag('link[rel="canonical"]', { rel: 'canonical', href: url });
    [['og:title', title], ['og:description', desc], ['og:url', url], ['og:type', post ? 'article' : 'website'], ['og:site_name', site.name], ['og:image', image]].forEach(([k, v]) => v !== undefined && setTag(`meta[property="${k}"]`, { property: k, content: v || '' }));
    [['twitter:card', image ? 'summary_large_image' : 'summary'], ['twitter:title', title], ['twitter:description', desc], ['twitter:image', image]].forEach(([k, v]) => setTag(`meta[name="${k}"]`, { name: k, content: v || '' }));
    if (seo.noindex) setTag('meta[name="robots"]', { name: 'robots', content: 'noindex, nofollow' });
    setTag('link[rel="preconnect"][href="https://fonts.gstatic.com"]', { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' });
    let ld = document.getElementById('site-jsonld'); if (!ld) { ld = document.createElement('script'); ld.id = 'site-jsonld'; ld.type = 'application/ld+json'; document.head.appendChild(ld); }
    const contact = site.pages?.flatMap((p) => p.sections || []).find((x) => x.type === 'contact')?.content || {};
    ld.textContent = JSON.stringify(post && !post.notfound
      ? { '@context': 'https://schema.org', '@type': 'Article', headline: postTitle, image: image || undefined, datePublished: post.published_at, author: { '@type': 'Organization', name: site.name }, publisher: { '@type': 'Organization', name: site.name, logo: site.navbar?.logo_url || undefined } }
      : { '@context': 'https://schema.org', '@type': seo.schema_type || 'Organization', name: site.name, url, logo: site.navbar?.logo_url || undefined, image: image || undefined, description: desc || undefined, telephone: contact.phone || undefined, email: contact.email || undefined, address: contact.address ? { '@type': 'PostalAddress', streetAddress: typeof contact.address === 'string' ? contact.address : pick(contact.address, effectiveLang) } : undefined });
    const trackPath = postSlug ? `/blog/${postSlug}` : `/${pageSlug || ''}`;
    if (!isPreview && site.is_published && trackedPath.current !== trackPath) { trackedPath.current = trackPath; trackEvent('pageview', trackPath); }
    const h = window.location.hash?.slice(1);
    if (h) { let tries = 0; const go = () => { const el = document.getElementById(h); if (el) { el.scrollIntoView({ behavior: 'smooth' }); setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: 'smooth' }), 1400); } else if (tries++ < 25) setTimeout(go, 120); }; setTimeout(go, 100); }
  }, [site, pageSlug, postSlug, post?.id, effectiveLang]); // eslint-disable-line

  const onNavigate = useCallback((e, href) => {
    if (!href) return;
    if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
    const [path, hash] = href.split('#');
    // scroll ulang setelah blok async (produk/blog) selesai dimuat agar tidak melewati target (layout shift)
    const scrollTo = (tries = 0) => { if (!hash) return; const el = document.getElementById(hash); if (el) { el.scrollIntoView({ behavior: 'smooth' }); if (tries < 25) setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 1400); } else if (tries < 25) setTimeout(() => scrollTo(tries + 1), 120); };
    const current = postSlug ? `blog/${postSlug}` : (pageSlug || 'home');
    const targetSlug = path ? (path.replace(/^\//, '') || 'home') : (document.getElementById(hash) ? current : 'home');
    if (hash && targetSlug === current) { e.preventDefault(); scrollTo(); return; }
    e.preventDefault();
    const clean = targetSlug === 'home' ? '' : targetSlug;
    const base = hostSite ? '' : `/site/${slug}`;
    navigate(`${base}/${clean}`.replace(/\/$/, '') || '/');
    window.scrollTo({ top: 0 });
    if (hash) setTimeout(() => scrollTo(), 150);
  }, [hostSite, slug, navigate, pageSlug, postSlug]);

  if (status === 'gate') return <PasswordGate info={gate.info} busy={gate.busy} error={gate.error} onSubmit={(pw) => { setGate((g) => ({ ...g, busy: true })); load(pw); }} />;
  if (status === 'loading') return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (status !== 'ok' || !site) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-6">
        <img src="/pillot-icon.png" alt="" className="h-12 w-12 mb-4 opacity-70" />
        <h1 className="text-2xl font-semibold text-slate-900">{status === 'notfound' ? 'Website tidak ditemukan' : 'Terjadi kesalahan'}</h1>
        <p className="mt-2 text-slate-500 text-sm">{status === 'notfound' ? 'Website ini belum dipublikasikan atau alamatnya salah. / This site is not published or the address is wrong.' : 'Silakan coba lagi.'}</p>
      </div>
    );
  }
  const home = site.pages?.find((p) => p.is_home) || site.pages?.[0];
  const page = postSlug ? home : (site.pages?.find((p) => (pageSlug ? p.slug === pageSlug : p.is_home)) || (pageSlug ? null : home));
  const notFound = !postSlug && !page;
  const postNotFound = postSlug && post?.notfound;
  const extra = postSlug
    ? (post === null ? <div className="flex justify-center py-32"><Loader2 className="animate-spin text-slate-400" /></div> : postNotFound ? <NotFoundView cfg={site.settings?.not_found} lang={effectiveLang} onNavigate={onNavigate} /> : <BlogPostView post={post} lang={effectiveLang} onNavigate={onNavigate} />)
    : notFound ? <NotFoundView cfg={site.settings?.not_found} lang={effectiveLang} onNavigate={onNavigate} /> : null;

  return (
    <div className="min-h-screen">
      {!site.is_published && <div className="bg-amber-100 text-amber-900 text-center text-xs py-1.5 font-medium">Mode pratinjau — website belum dipublikasikan</div>}
      <SiteRenderer site={site} page={page || home} lang={effectiveLang} setLang={setLang} onNavigate={onNavigate} injectAnalytics={site.is_published} company={site.company} storeSlug={site.company?.slug}>{extra}</SiteRenderer>
      <WhatsAppFloat cfg={site.navbar?.whatsapp} lang={effectiveLang} siteName={site.name} hasLivechat={!!site.livechat} />
      <LeadPopup cfg={site.settings?.popup} lang={effectiveLang} siteSlug={site.slug} isPreview={isPreview || !site.is_published} />
    </div>
  );
}
